import { omit } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'

import { QuantityContext } from './Cart/CartContext'
import translate from '../../utils/translate'

// https://github.com/oliviertassinari/babel-plugin-transform-react-remove-prop-types/blob/v0.4.15/README.md#is-it-safe
export const QuantityFieldPropTypes = {
  dispatch: PropTypes.func,
  lineItem: PropTypes.object,
  min: PropTypes.number,
  quantity: PropTypes.number,
  t: PropTypes.func.isRequired,
  withLabel: PropTypes.bool,
  invalidProduct: PropTypes.bool,
}

function QuantityField(props) {
  const { min = 1, quantity = 1, withLabel = false, t, invalidProduct = false } = props
  const quantityScope = React.useContext(QuantityContext)

  const htmlAttributes = omit(props, Object.keys(QuantityFieldPropTypes))
  const className = htmlAttributes.className

  const inputRef = React.useRef()
  const [value, setValue] = React.useState(quantity)
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    setValue(quantity)
  }, [quantity])

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) handleBlur(event)
  }

  const setQuantity = (quantity) => {
    const prevQuantity = quantityScope.getQuantity()
    const normalizedQuantity = Number.isInteger(quantity) ? Math.max(quantity, min) : min

    if (prevQuantity !== normalizedQuantity) {
      setIsLoading(true)
      quantityScope
        .setQuantity(normalizedQuantity, normalizedQuantity - prevQuantity)
        // Catch it so we do not get an unhandled rejection error.
        .catch(() => {})
        .finally(() => setIsLoading(false))
    }
  }

  const handleBlur = (event) => {
    if (event.target.value.length === 0) setValue(quantity.toString())

    if (!isLoading) {
      const quantity = parseInt(event.target.value, 10)
      setQuantity(quantity)
    }
  }

  const handleChange = (event) => {
    const inputValue = event.target.value

    if (min > 0 && !inputValue.startsWith('0') && /^(\d+)?$/.test(inputValue)) {
      setValue(inputValue)
    } else if (min <= 0 && /^(\d+)?$/.test(inputValue)) {
      setValue(inputValue)
    }
  }

  const handleFocus = (event) => {
    event.target.setSelectionRange(0, event.target.value.length)
  }

  const changeQuantity = (delta) => {
    const quantity = quantityScope.getQuantity() + delta
    setQuantity(quantity)
    // Waiting for all event handlers to finish, then blur the input field.
    // This solves an issue of undesired focus states on touch devices (UNITY-5984).
    setTimeout(() => inputRef.current && inputRef.current.blur())
  }

  const input = (
    <div className={className}>
      <button onClick={() => changeQuantity(-1)} disabled={isLoading || invalidProduct} />
      <input
        ref={inputRef}
        id="quantity"
        type="tel"
        min={min}
        value={value.toString()}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        autoComplete="off"
        disabled={isLoading || invalidProduct}
      />
      <button onClick={() => changeQuantity(+1)} disabled={isLoading || invalidProduct} />
    </div>
  )

  return withLabel ? (
    <label htmlFor="quantity" {...htmlAttributes} className={`${className}-label`}>
      <span>{t('components.productComponent.productQuantity')}</span>
      {input}
    </label>
  ) : (
    input
  )
}

QuantityField.propTypes = QuantityFieldPropTypes

export default translate()(QuantityField)
